import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  ArrowLeft, 
  Calendar, 
  Package, 
  User, 
  Tag,
  FileText,
  Loader2,
  AlertTriangle,
  Beaker,
  Clock,
  CheckCircle2,
  FlaskConical,
  ChevronDown,
  ChevronUp,
  Building2,
  Hash,
  TestTube,
  Clipboard,
  BadgeCheck,
  XCircle,
  Info,
  Layers,
  FileSpreadsheet,
  FlaskRound,
  CircleDot,
  Scale,
  Download
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion, AnimatePresence } from "framer-motion";
import { generateIterationPDF } from "@/utils/pdfGenerator";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Status badge colors
const STATUS_COLORS = {
  "Not Started": "bg-slate-100 text-slate-700 border-slate-200",
  "Started": "bg-blue-100 text-blue-700 border-blue-200",
  "End": "bg-amber-100 text-amber-700 border-amber-200",
  "Complete": "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Finish": "bg-purple-100 text-purple-700 border-purple-200",
};

const STATUS_ICONS = {
  "Not Started": Clock,
  "Started": FlaskConical,
  "End": AlertTriangle,
  "Complete": CheckCircle2,
  "Finish": CheckCircle2,
};

// Iteration result colors
const RESULT_COLORS = {
  "Approved": "bg-emerald-100 text-emerald-700 border-emerald-300",
  "Rejected": "bg-red-100 text-red-700 border-red-300",
  "Pending": "bg-amber-100 text-amber-700 border-amber-300",
  "Under Review": "bg-blue-100 text-blue-700 border-blue-300",
};

// Info field component - defined outside to avoid re-creation on every render
const InfoField = ({ icon: Icon, label, value, className = "" }) => (
  <div className={`flex items-start gap-3 ${className}`}>
    <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
      <Icon className="w-4 h-4 text-slate-500" />
    </div>
    <div>
      <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-slate-800 font-poppins mt-0.5">{value || "-"}</p>
    </div>
  </div>
);

export default function ProductDevelopmentReportDetailPage() {
  const navigate = useNavigate();
  const { folderId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIterations, setExpandedIterations] = useState({});
  const [downloadingPDF, setDownloadingPDF] = useState({});

  useEffect(() => {
    fetchProductDetails();
  }, [folderId]);

  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/product-folders/${folderId}`);
      if (response.data) {
        // Handle both direct data and wrapped data (response.data.folder)
        const folderData = response.data.folder || response.data;
        setProduct(folderData);
        // Expand all iterations by default
        const expanded = {};
        folderData.iterations?.forEach((_, idx) => {
          expanded[idx] = true;
        });
        setExpandedIterations(expanded);
      }
    } catch (error) {
      toast.error("Failed to load product details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF download for a specific iteration
  const handleDownloadPDF = async (iteration, idx) => {
    setDownloadingPDF(prev => ({ ...prev, [idx]: true }));
    try {
      const filename = await generateIterationPDF(product, iteration);
      toast.success(`PDF downloaded: ${filename}`, {
        description: `Iteration ${iteration.iterationNo} exported successfully`,
        duration: 3000
      });
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error("Failed to generate PDF", {
        description: "Please try again later"
      });
    } finally {
      setDownloadingPDF(prev => ({ ...prev, [idx]: false }));
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Format date with time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return dateStr;
    }
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const colorClass = STATUS_COLORS[status] || "bg-gray-100 text-gray-700 border-gray-200";
    const StatusIcon = STATUS_ICONS[status] || Clock;
    return (
      <Badge className={`${colorClass} border font-poppins font-medium px-3 py-1.5 flex items-center gap-1.5`}>
        <StatusIcon className="w-4 h-4" />
        {status || "Not Started"}
      </Badge>
    );
  };

  // Get result badge
  const getResultBadge = (result) => {
    const colorClass = RESULT_COLORS[result] || "bg-gray-100 text-gray-700 border-gray-200";
    return (
      <Badge className={`${colorClass} border font-poppins font-medium px-2.5 py-1`}>
        {result || "Pending"}
      </Badge>
    );
  };

  // Toggle iteration expansion
  const toggleIteration = (idx) => {
    setExpandedIterations(prev => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-vcc-cream-50 to-white">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-vcc-teal animate-spin mx-auto mb-4" />
          <p className="text-slate-600 font-poppins">Loading product development report...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-vcc-cream-50 to-white">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-poppins font-semibold text-slate-800 mb-2">Product Not Found</h2>
          <p className="text-slate-500 font-poppins mb-4">The requested product could not be found.</p>
          <Button onClick={() => navigate("/product-development-report")} className="font-poppins">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Report
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-vcc-cream-50 via-white to-vcc-pink-50 p-6">
      {/* Back Button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/product-development-report")}
        className="mb-6 font-poppins text-slate-600 hover:text-vcc-teal"
        data-testid="back-to-report"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Product Development Report
      </Button>

      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-gradient-to-br from-vcc-teal to-vcc-teal/70 rounded-xl flex items-center justify-center shadow-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <Badge variant="outline" className="font-poppins bg-vcc-teal/10 text-vcc-teal border-vcc-teal/30">
                  {product.sampleNo}
                </Badge>
                {getStatusBadge(product.testStatus)}
              </div>
              <h1 className="text-2xl font-poppins font-bold text-slate-800 mt-2">
                {product.productName}
              </h1>
              <p className="text-slate-500 font-poppins mt-1 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                {product.clientName || "No client specified"}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider">Total Iterations</p>
            <p className="text-3xl font-bold text-vcc-teal font-poppins mt-1">
              {product.iterations?.length || 0}
            </p>
          </div>
        </div>
      </div>

      {/* Product Basic Information */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 mb-6">
        <h2 className="text-lg font-poppins font-semibold text-slate-800 mb-6 flex items-center gap-2">
          <Info className="w-5 h-5 text-vcc-teal" />
          Product Information
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <InfoField icon={Hash} label="Sample No" value={product.sampleNo} />
          <InfoField icon={Package} label="Product Name" value={product.productName} />
          <InfoField icon={Tag} label="Product Type" value={product.productType} />
          <InfoField icon={FileSpreadsheet} label="SKU" value={product.sku} />
          <InfoField icon={User} label="Client Name" value={product.clientName} />
          <InfoField icon={Layers} label="Product Category" value={product.productCategory} />
          <InfoField icon={BadgeCheck} label="Test Priority" value={product.testPriority} />
          <InfoField icon={TestTube} label="Test Method" value={product.testMethod} />
          <InfoField icon={User} label="Assigned Lab Assistant" value={product.assignedLabAssistant} />
          <InfoField icon={Calendar} label="Created Date" value={formatDateTime(product.createdDateTime || product.portalCreatedAt)} />
          <InfoField icon={Calendar} label="Test Started At" value={formatDateTime(product.testStartedAt)} />
          <InfoField icon={Clock} label="Development Status" value={product.developmentStatus} />
        </div>

        {/* Approval Information */}
        {product.approvalStatus && (
          <div className="mt-6 pt-6 border-t border-slate-100">
            <h3 className="text-sm font-poppins font-semibold text-slate-700 mb-4">Approval Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <InfoField icon={BadgeCheck} label="Approval Status" value={product.approvalStatus} />
              <InfoField icon={User} label="Approved By" value={product.approvedBy} />
              <InfoField icon={Calendar} label="Approved At" value={formatDateTime(product.approvedAt)} />
            </div>
          </div>
        )}
      </div>

      {/* Iterations Section */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-poppins font-semibold text-slate-800 flex items-center gap-2">
            <FlaskConical className="w-5 h-5 text-vcc-teal" />
            Iterations History
          </h2>
          <Badge className="bg-purple-100 text-purple-700 font-poppins font-semibold px-3 py-1.5">
            {product.iterations?.length || 0} Total Iterations
          </Badge>
        </div>

        {!product.iterations || product.iterations.length === 0 ? (
          <div className="text-center py-12 bg-slate-50 rounded-xl">
            <Beaker className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-poppins">No iterations recorded yet</p>
          </div>
        ) : (
          <div className="space-y-4">
            {product.iterations.map((iteration, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="border border-slate-200 rounded-xl overflow-hidden"
              >
                {/* Iteration Header */}
                <div
                  className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-white cursor-pointer hover:bg-slate-100/50 transition-colors"
                  onClick={() => toggleIteration(idx)}
                  data-testid={`iteration-header-${iteration.iterationNo}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-vcc-pink-100 to-vcc-cream-100 rounded-xl flex items-center justify-center">
                      <span className="text-lg font-bold text-vcc-teal font-poppins">T{iteration.iterationNo}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-poppins font-semibold text-slate-800">
                          Iteration {iteration.iterationNo}
                        </h3>
                        {iteration.formulaVersion && (
                          <Badge variant="outline" className="font-poppins text-xs">
                            {iteration.formulaVersion}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-500 font-poppins flex items-center gap-2 mt-0.5">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(iteration.date)}
                        {iteration.createdBy && (
                          <>
                            <span className="mx-1">•</span>
                            <User className="w-3.5 h-3.5" />
                            {iteration.createdBy}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Download PDF Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadPDF(iteration, idx);
                      }}
                      disabled={downloadingPDF[idx]}
                      className="font-poppins border-vcc-teal/30 text-vcc-teal hover:bg-vcc-teal/10 hover:border-vcc-teal"
                      data-testid={`download-pdf-iteration-${iteration.iterationNo}`}
                    >
                      {downloadingPDF[idx] ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      <span className="ml-1.5 hidden sm:inline">Download PDF</span>
                    </Button>
                    {getResultBadge(iteration.result)}
                    {iteration.testStatus && (
                      <Badge className={`${STATUS_COLORS[iteration.testStatus] || 'bg-gray-100'} border font-poppins`}>
                        {iteration.testStatus}
                      </Badge>
                    )}
                    {expandedIterations[idx] ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>

                {/* Iteration Details */}
                <AnimatePresence>
                  {expandedIterations[idx] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="p-5 bg-white border-t border-slate-100">
                        {/* Basic Iteration Info */}
                        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                          <InfoField icon={Hash} label="Iteration No" value={`T${iteration.iterationNo}`} />
                          <InfoField icon={Tag} label="SKU" value={iteration.sku} />
                          <InfoField icon={Calendar} label="Date" value={formatDate(iteration.date)} />
                          <InfoField icon={User} label="Created By" value={iteration.createdBy} />
                        </div>

                        {/* Formula/Product Details */}
                        {iteration.productDetails && iteration.productDetails.length > 0 && (
                          <div className="mb-6">
                            <h4 className="text-sm font-poppins font-semibold text-slate-700 mb-3 flex items-center gap-2">
                              <FlaskRound className="w-4 h-4 text-vcc-teal" />
                              Formula Details ({iteration.productDetails.length} ingredients)
                            </h4>
                            <div className="bg-slate-50 rounded-xl overflow-hidden overflow-x-auto">
                              <table className="w-full text-sm min-w-[800px]">
                                <thead>
                                  <tr className="bg-slate-100/80">
                                    <th className="text-left p-3 font-poppins font-semibold text-slate-700 w-8">#</th>
                                    <th className="text-left p-3 font-poppins font-semibold text-slate-700">Ingredient</th>
                                    <th className="text-left p-3 font-poppins font-semibold text-slate-700">INCI Name</th>
                                    <th className="text-right p-3 font-poppins font-semibold text-slate-700">Composition (%)</th>
                                    <th className="text-left p-3 font-poppins font-semibold text-slate-700">Function</th>
                                    <th className="text-left p-3 font-poppins font-semibold text-slate-700">Supplier</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {iteration.productDetails.map((detail, dIdx) => (
                                    <tr key={dIdx} className="border-t border-slate-200/50 hover:bg-white/50">
                                      <td className="p-3 font-poppins text-slate-500 text-center">{dIdx + 1}</td>
                                      <td className="p-3 font-poppins text-slate-800 font-medium">{detail.items || detail.ingredient || detail.name || "-"}</td>
                                      <td className="p-3 font-poppins text-slate-600">{detail.inci || detail.tradeName || "-"}</td>
                                      <td className="p-3 font-poppins text-slate-600 text-right font-medium">{detail.composition || detail.quantity || detail.percentage || "-"}</td>
                                      <td className="p-3 font-poppins text-slate-600">{detail.function || "-"}</td>
                                      <td className="p-3 font-poppins text-slate-600">{detail.vendorDetails || detail.supplier || "-"}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                        {/* Testing Details */}
                        {iteration.testingDetails && Object.keys(iteration.testingDetails).length > 0 && (
                          <div className="mb-6">
                            <h4 className="text-sm font-poppins font-semibold text-slate-700 mb-3 flex items-center gap-2">
                              <TestTube className="w-4 h-4 text-vcc-teal" />
                              Testing Details
                            </h4>
                            
                            {/* Quick stats */}
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                              {iteration.testingDetails.phInitial && (
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase">pH Initial</p>
                                  <p className="text-lg font-bold text-slate-800 font-poppins">{iteration.testingDetails.phInitial}</p>
                                </div>
                              )}
                              {iteration.testingDetails.phFinal && (
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase">pH Final</p>
                                  <p className="text-lg font-bold text-slate-800 font-poppins">{iteration.testingDetails.phFinal}</p>
                                </div>
                              )}
                              {iteration.testingDetails.phRange && (
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase">pH Range</p>
                                  <p className="text-lg font-bold text-slate-800 font-poppins">{iteration.testingDetails.phRange}</p>
                                </div>
                              )}
                              {iteration.testingDetails.viscosity && (
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase">Viscosity</p>
                                  <p className="text-lg font-bold text-slate-800 font-poppins">{iteration.testingDetails.viscosity}</p>
                                </div>
                              )}
                              {iteration.testingDetails.color && (
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase">Color</p>
                                  <p className="text-sm font-medium text-slate-800 font-poppins">{iteration.testingDetails.color}</p>
                                </div>
                              )}
                              {iteration.testingDetails.texture && (
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase">Texture</p>
                                  <p className="text-sm font-medium text-slate-800 font-poppins">{iteration.testingDetails.texture}</p>
                                </div>
                              )}
                              {iteration.testingDetails.odour && (
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase">Odour</p>
                                  <p className="text-sm font-medium text-slate-800 font-poppins">{iteration.testingDetails.odour}</p>
                                </div>
                              )}
                              {iteration.testingDetails.testResult && (
                                <div className="bg-white rounded-lg p-3 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase">Test Result</p>
                                  <Badge className={`mt-1 ${iteration.testingDetails.testResult === 'Pass' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                                    {iteration.testingDetails.testResult}
                                  </Badge>
                                </div>
                              )}
                            </div>

                            {/* Description & Direction of Use */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              {iteration.testingDetails.description && (
                                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider mb-2">Description</p>
                                  <p className="text-sm text-slate-700 font-poppins">{iteration.testingDetails.description}</p>
                                </div>
                              )}
                              {iteration.testingDetails.directionOfUse && (
                                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                                  <p className="text-xs text-slate-500 font-poppins uppercase tracking-wider mb-2">Direction of Use</p>
                                  <p className="text-sm text-slate-700 font-poppins">{iteration.testingDetails.directionOfUse}</p>
                                </div>
                              )}
                            </div>

                            {/* Method of Preparation */}
                            {iteration.testingDetails.methodOfPreparation && (
                              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100 mb-4">
                                <p className="text-xs text-blue-600 font-poppins uppercase tracking-wider mb-2 font-semibold">Method of Preparation</p>
                                <p className="text-sm text-slate-700 font-poppins whitespace-pre-wrap max-h-64 overflow-y-auto">{iteration.testingDetails.methodOfPreparation}</p>
                              </div>
                            )}

                            {/* Observation */}
                            {iteration.testingDetails.observation && (
                              <div className="bg-purple-50 rounded-lg p-4 border border-purple-100 mb-4">
                                <p className="text-xs text-purple-600 font-poppins uppercase tracking-wider mb-2 font-semibold">Observation</p>
                                <p className="text-sm text-slate-700 font-poppins whitespace-pre-wrap">{iteration.testingDetails.observation}</p>
                              </div>
                            )}

                            {/* Testing Remarks */}
                            {iteration.testingDetails.remarks && (
                              <div className="bg-amber-50 rounded-lg p-4 border border-amber-100">
                                <p className="text-xs text-amber-600 font-poppins uppercase tracking-wider mb-2 font-semibold">Testing Remarks</p>
                                <p className="text-sm text-slate-700 font-poppins whitespace-pre-wrap max-h-48 overflow-y-auto">{iteration.testingDetails.remarks}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Iteration Remarks (separate from testing remarks) */}
                        {iteration.remarks && (
                          <div className="mb-4">
                            <h4 className="text-sm font-poppins font-semibold text-slate-700 mb-2 flex items-center gap-2">
                              <Clipboard className="w-4 h-4 text-vcc-teal" />
                              Iteration Remarks
                            </h4>
                            <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                              <p className="text-sm text-slate-700 font-poppins whitespace-pre-wrap">{iteration.remarks}</p>
                            </div>
                          </div>
                        )}

                        {/* Sync Status */}
                        <div className="flex items-center justify-between pt-4 border-t border-slate-100 mt-4">
                          <div className="flex items-center gap-2">
                            <CircleDot className={`w-4 h-4 ${
                              iteration.zohoSyncStatus === 'Synced' ? 'text-emerald-500' : 
                              iteration.zohoSyncStatus === 'Failed' ? 'text-red-500' : 'text-amber-500'
                            }`} />
                            <span className="text-xs text-slate-500 font-poppins">
                              Sync Status: {iteration.zohoSyncStatus || 'Pending'}
                            </span>
                            {iteration.zohoSyncedAt && (
                              <span className="text-xs text-slate-400 font-poppins">
                                • Synced: {formatDateTime(iteration.zohoSyncedAt)}
                              </span>
                            )}
                          </div>
                          {iteration.zohoRecordId && (
                            <span className="text-xs text-slate-400 font-poppins">
                              Zoho ID: {iteration.zohoRecordId}
                            </span>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
